// The voice a RUN pays for — CCT-310.
//
// Two kinds of line cannot be voiced by the author, because their text does not exist until a
// run exists:
//
//   * `instance` (`$forename`, `$name`) — rendered in the background when the player starts,
//     attached to their run. A scene holding one waits for the render to COMPLETE before it
//     plays: half the lines voiced and half silent does not read as loading, it reads as broken.
//   * `runtime` (`$US.status`) — asked for at play time, because the status moves during the run.
//
// ⚠️ NOTHING HERE MAY BLOCK A SCENE FOR EVER. Every failure resolves to "play it silently": the
// voice is an enrichment, and a mission frozen on a spinner is worse than a mission read on
// screen. That is why `ready` treats a render stopped by the spending cap as finished — it will
// not progress, and waiting on it would wait for ever.
import { apiFetch } from './client';

/** State of the launch render, as the server reports it. */
export interface RunAudioState {
  /** `absent` (never asked for) · `en_cours` · `termine` · `erreur` · `indisponible`. */
  statut: string;
  total: number;
  faits: number;
  echecs: number;
  chars: number;
  /** The render STOPPED on the spending cap rather than finishing. */
  plafonne: boolean;
  /** Server's verdict: may a scene holding personalised lines start? */
  pret: boolean;
  erreur?: string | null;
}

/**
 * Reads the state of the run's voice. Never throws.
 *
 * ⚠️ AN ERROR RESOLVES TO `pret: true`, DELIBERATELY. If the server cannot be reached, the
 * alternative is a scene that never starts. Playing it silently loses the voice; waiting loses
 * the mission.
 */
export async function readRunAudio(
  token: string | null, missionId: number,
): Promise<RunAudioState> {
  try {
    return await apiFetch(`/me/stories/${missionId}/audio-run`, { token, auth: true });
  } catch {
    return { statut: 'indisponible', total: 0, faits: 0, echecs: 0, chars: 0,
             plafonne: false, pret: true };
  }
}

/**
 * Audio of ONE `runtime` line, asked for at the moment of playing it.
 *
 * ⚠️ WE DESIGNATE THE LINE, WE NEVER SEND THE TEXT. The server re-reads it in the story,
 * checks it really is a `runtime` line, and resolves it itself. Sending the text would turn this
 * into a voice generator, billed per character, open to anyone holding a token.
 *
 * ⚠️ AND A REPLAY COSTS NOTHING. The server fingerprints the RESOLVED text: replaying with an
 * unchanged status returns the audio already produced without calling the provider. So the app
 * may call this again freely — it must not cache it itself, or a status that moved would keep
 * playing a sentence that has become false.
 *
 * Returns the blob key, or `null` when the line cannot be voiced (no engine, line longer than
 * the per-playback cap). `null` means "display the text and move on", never "retry".
 */
export async function fetchLineAudio(
  token: string | null, missionId: number, itemId: string, path: string,
): Promise<{ key: string; cached: boolean } | null> {
  try {
    const query = `item=${encodeURIComponent(itemId)}&path=${encodeURIComponent(path)}`;
    const r = await apiFetch(`/me/stories/${missionId}/line-audio?${query}`, { token, auth: true });
    const url = String(r?.url ?? '').trim();
    if (!url) return null;
    return { key: url, cached: !!r?.cached };
  } catch {
    // A refusal is EXPECTED here (409): no engine, or a line above the cap. It is not an
    // incident, and the reader must not present it as one.
    return null;
  }
}
